<template>
  <div
    class="aha-slide-notes relative"
    v-if="isBackstage || !isPresenting"
  >
    <div class="slide-notes__content-wrapper" :class="{'is-backstage': isBackstage}" id="aha-slide-notes">
      <textarea
        ref="textarea"
        class="notes-input"
        :disabled="isDisableNote"
        :placeholder="$i18nPresenter.t('Type your note for this slide…')"
        :value="slideActive.notes"
        @input="handleChange"
        @click="handleClick"
        @focus="onFocusSlideNoteInput"
        @blur="onBlurSlideNoteInput"
        v-click-outside="handleClickOutside"
      />
      <span
        v-if="!isBackstage && canUseRemoteControl"
        class="absolute top-2 right-4 text-xs pl-1 text-base-80 bg-base-0 rounded-sm w-max"
      >
        {{ $i18nPresenter.t('Notes will be shown in Remote Control') }}
      </span>
      <span
        v-if="!isBackstage && !canUseRemoteControl"
        class="absolute top-2 right-4 text-xs pl-1 text-base-80 bg-base-0 rounded-sm w-max"
      >
        {{ $i18nPresenter.t('Notes will be shown in') }}
        <a class="text-link--primary underline" @click="openBackstageWindow">
          {{ $i18nPresenter.t('Backstage') }}
        </a>
        <aha-icon
          name="open-new-window"
          class="text-base-50"
          width="18"
          height="18"
          viewBox="-4 -4 32 32"
        />
      </span>
    </div>
  </div>
</template>

<script>

import { mapState, mapActions, mapGetters } from 'vuex'
import * as Sentry from '@sentry/vue'
import { debounce } from 'lodash'
import SlideApi from '@/api/slide'
import EventBus from '@/eventBus'
import utils from '@/util'
import Socket from '@/socket'
import config from '@/config'
import constants from '@/constant'

export default {
  props: {
    initialContent: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      showTooltip: false,
      allowShowSlideType: [
        'wordCloud',
        'imageChoice',
        'multipleChoice',
        'question',
        'leaderboard',
        'spinnerWheel',
      ],
    }
  },
  computed: {
    ...mapGetters(['isBackstage', 'isPresenting']),
    ...mapGetters('remoteControlLink', ['canUseRemoteControl']),
    ...mapState({
      slideActive: (state) => state.slideActive,
      presentation: (state) => state.presentation,
      presentationEditors: (state) => state.presentationEditors,
      isDisableEditPresentation: (state) => state.isDisableEditPresentation,
      canEditPresentation: (state) => state.canEditPresentation,
      currentUser: (state) => state.currentUser,
    }),
    isEURegion() {
      return config.region === 'eu'
    },
    slideTypeInPPTAddIn() {
      if (this.slideActive && ['spinnerWheel'].includes(this.slideActive.type)) {
        return true
      }
      return false
    },
    slideTypesAllowToolbar() {
      return !this.allowShowSlideType.includes(this.slideActive.type) &&
          !(this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === null) &&
          !(this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'imageChoice') &&
          !this.slideTypeInPPTAddIn
    },
    isToolbarVisible() {
      return !this.isBackstage && !this.isDisableEditPresentation &&
          (this.slideTypesAllowToolbar || this.isEURegion)
      && this.slideActive.type === 'freestyle'
    },
    isDisableNote() {
      return !this.canEditPresentation
    },
    showAddInPPT() {
      if (this.$store.state.sharePresentation) {
        return false
      }
      if (this.$store.state.workingMode === constants.workspace.TEAM) {
        return false
      }
      const isShowButtonWithCollab = !this.isDisableEditPresentation

      return config.region !== 'eu' && !this.isBackstage && isShowButtonWithCollab &&
          (this.allowShowSlideType.includes(this.slideActive.type) ||
              (this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === null) ||
              (this.slideActive.type === 'pickAnswer' && this.slideActive.slideType === 'imageChoice')
          )
    },
    showNoteCanEditThisSlide() {
      return this.presentationEditors.length > 1 ||
          (this.presentationEditors.length === 1 &&
              this.presentationEditors[0].id !== this.presentation.takenOverBy)
    },
    isCollabNote() {
      return this.showNoteCanEditThisSlide && (this.showAddInPPT || this.isToolbarVisible)
    },
  },
  mounted() {
    EventBus.$on('slide-notes:focus-input', () => {
      if (this.slideActive.notes) {
        if (this.$refs.textarea) this.$refs.textarea.focus()
      }
    })
  },

  destroyed() {
    EventBus.$off('slide-notes:focus-input')
  },
  methods: {
    ...mapActions(['updateSlideActiveAction']),
    handleClick() {
      // https://floating-vue.starpad.dev/legacy/v2/#directive
      if (!this.slideActive.notes) {
        // this.showTooltip = true
      }
      utils.mixpanelTracking('Presentation Editor', 'Slide Notes > Click', this.slideActive.id)
    },
    handleChange: debounce(async function (evt) {
      if (this.showTooltip) this.showTooltip = false

      const { value: notes } = evt.target
      this.updateSlideActiveAction({
        notes,
      })

      // set sync presentation
      this.$store.dispatch('setSyncPresentation', 1)
      try {
        await SlideApi.patchSlideById(this.slideActive.id, {
          socketId: Socket.getSocketId(),
          notes,
        })
        this.$store.dispatch('setSyncPresentation', 2)
      } catch (e) {
        Sentry.captureException(e)
        this.$store.dispatch('setSyncPresentation', 3)
      }
    }, 300),
    handleClickOutside() {
      if (this.showTooltip) this.showTooltip = false
      this.$store.state.slideActive.isFocusingSlideNoteInput = false
    },
    onFocusSlideNoteInput() {
      this.$store.state.slideActive.isFocusingSlideNoteInput = true
    },
    onBlurSlideNoteInput() {
      this.$store.state.slideActive.isFocusingSlideNoteInput = false
    },
    openBackstageWindow() {
      EventBus.$emit('present-with-backstage')
    },
  },
}
</script>

<style lang="scss">
// no scoped to target tooltip

.aha-slide-notes {
  @apply my-0 mx-3;
  width: calc(100% - 1.5rem);
  height: 100%;

  .notes-input {
    @apply appearance-none w-full;
    @apply px-3 py-2 text-sm border border-base-30;
    @apply focus:outline-none;
    @apply placeholder-base-70;
    @apply rounded-tl-lg rounded-tr-lg;
    line-height: 18px;
    background: #fff;
    resize: none;
    height: 100%;
    padding-right: 265px;
    &:hover, &:focus {
      border: 1px solid #6A1EBB;
    }
  }
  .trigger {
    @apply w-full h-full;
  }
}

.tooltip-inner {
    padding: 10px;
    font-size: 14px;
    line-height: 18px;
  }

.slide-notes-tooltip {
  width: 300px;

  hr {
    margin: 6px 0;
    color: rgba(255, 255, 255, 0.1);
  }

  .tooltip-arrow {
    left: 0 !important;
  }

  .slide-notes-badge {
    background: #525F7F;
    border-radius: 17px;
    padding: 3px 5px;
    font-weight: bold;
    font-size: 8px;
    text-transform: uppercase;
  }
}
.slide-notes__content-wrapper {
  @apply absolute bottom-0 w-full;
  max-height: 84px;
  min-height: 53px;
  display: flex;
  box-shadow: 0px 10px 38px 0px #525F7F08;

  &.is-backstage {
    height: 100%;
    max-height: 100%;
  }
}
</style>
